import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FileDown, Save, Trophy, TrendingUp, Users2, Calendar } from "lucide-react";

const monthNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const fmtTl = (n) => `₺ ${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString("tr-TR");

export default function FieldSupervisors() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(monthNow());
  const [dash, setDash] = useState(null);
  const [periods, setPeriods] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, pp] = await Promise.all([
        api.get("/field-supervisors/dashboard", { params: { month } }),
        api.get("/field-supervisors/passenger-periods", { params: { month } }),
      ]);
      setDash(d.data);
      setPeriods(pp.data);
      // Draft'ları önerilen değerlerle doldur (kaydedilmemişse)
      const dr = {};
      (pp.data.periods || []).forEach((p) => {
        if (!p.saved && p.suggested_from_daily > 0) dr[p.period] = p.suggested_from_daily;
      });
      setDrafts(dr);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const savePeriod = async (period) => {
    const val = parseInt(drafts[period] ?? "", 10);
    if (isNaN(val) || val < 0) return toast.error("Geçerli bir yolcu sayısı girin");
    setSaving(period);
    try {
      await api.post("/field-supervisors/passenger-periods", { month, period, ridership: val });
      toast.success(`Dönem ${period} kaydedildi (${fmtInt(val)} yolcu)`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(null);
    }
  };

  const downloadKarne = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/field-supervisors/karne.pdf", { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `saha-karne-${month}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF indirilemedi");
    } finally { setDownloading(false); }
  };

  const growthColor = (g) => g == null ? "text-slate-500" : g >= 0 ? "text-emerald-700" : "text-red-700";

  return (
    <div className="space-y-6" data-testid="field-supervisors-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Saha Sorumluları</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Saha Sorumluları Prim</h1>
          <p className="mt-1 text-sm text-slate-500">10 günlük dönemler halinde yolcu girişi. Prim, önceki yılın aynı ayına göre büyümeye kademeli olarak bağlanır.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-[160px] font-mono" data-testid="fs-month" />
          <Button onClick={downloadKarne} disabled={downloading || !dash} variant="outline" data-testid="fs-karne-btn">
            <FileDown className="w-4 h-4 mr-1" /> Karne PDF
          </Button>
        </div>
      </div>

      {/* Dashboard KPI */}
      {dash && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Bu Ay Yolcu</div>
            <div className="mt-1 text-2xl font-bold font-mono text-slate-900">{fmtInt(dash.current_ridership)}</div>
            <div className="text-[10px] text-slate-400 font-mono uppercase mt-1">Kaynak: {dash.current_source}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Geçen Yıl Aynı Ay</div>
            <div className="mt-1 text-2xl font-bold font-mono text-slate-900">{fmtInt(dash.prior_ridership)}</div>
            <div className="text-[10px] text-slate-400 font-mono uppercase mt-1">{dash.prior_month}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Büyüme</div>
            <div className={`mt-1 text-2xl font-bold font-mono flex items-center gap-1 ${growthColor(dash.growth_pct)}`}>
              <TrendingUp className="w-4 h-4" /> {dash.growth_pct == null ? "—" : `%${dash.growth_pct}`}
            </div>
            <div className="text-[10px] text-slate-400 font-mono uppercase mt-1" data-testid="fs-active-tier">Kademe: {dash.active_tier?.tier}{dash.active_tier?.data_missing ? " · veri yok" : ""}</div>
          </CardContent></Card>
          <Card className="border-blue-200 bg-blue-50/50"><CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-blue-700 font-mono">Kişi Başı Net Prim</div>
            <div className="mt-1 text-2xl font-bold font-mono text-blue-900 flex items-center gap-1" data-testid="fs-per-person-tl">
              <Trophy className="w-4 h-4" /> {fmtTl(dash.per_person_net_tl ?? dash.total_tl)}
            </div>
            <div className="text-[10px] text-blue-500 font-mono uppercase mt-1">Baz {fmtTl(dash.base_tl)} + Kademe {fmtTl(dash.tier_bonus_tl)}</div>
            <div className="mt-2 pt-2 border-t border-blue-200 text-[10px] text-blue-800 uppercase tracking-wider font-mono">
              {dash.supervisor_count ?? 2} kişi toplam: <span className="font-bold text-blue-900" data-testid="fs-company-total-tl">{fmtTl(dash.company_total_tl)}</span>
            </div>
          </CardContent></Card>
        </div>
      )}

      {/* Update 29.2 — Şeffaf hesap satırı + Unmarked days rozeti + Kilit bilgisi */}
      {dash && (
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs md:text-sm text-slate-700 leading-relaxed font-mono" data-testid="fs-breakdown-text">
                {dash.breakdown_text || "—"}
              </div>
              <div className="flex items-center gap-2">
                {dash.locked && (
                  <span className="text-[10px] uppercase font-mono text-purple-700 bg-purple-100 px-2 py-1 rounded" data-testid="fs-locked-badge">
                    Kilitli — Snapshot
                  </span>
                )}
                {(dash.unmarked_days_count ?? 0) > 0 && (
                  <span className="text-[10px] uppercase font-mono text-amber-800 bg-amber-100 px-2 py-1 rounded" data-testid="fs-unmarked-days-badge">
                    Bu ay {dash.unmarked_days_count} gün işaretlenmemiş
                  </span>
                )}
                {dash.active_tier?.data_missing && (
                  <span className="text-[10px] uppercase font-mono text-red-800 bg-red-100 px-2 py-1 rounded" data-testid="fs-no-prior-badge">
                    Geçen yılın aynı ayı için yolcu verisi yok
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3-period passenger entry */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold flex items-center gap-2"><Users2 className="w-4 h-4" /> Aylık Yolcu Girişi — 3 Dönem</h3>
            {periods && (
              <div className="text-sm">
                Toplam: <span className="font-mono font-bold text-slate-900">{fmtInt(periods.total)}</span>
                {periods.has_any_period && <span className="ml-2 text-[10px] uppercase font-mono text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Kaynak: 3 Dönem</span>}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(periods?.periods || []).map((p) => (
              <div key={p.period} className={`border rounded-lg p-4 ${p.saved ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}`} data-testid={`period-${p.period}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-slate-600 font-mono flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Dönem {p.period}
                  </Label>
                  <span className="text-[10px] font-mono text-slate-500">Gün {p.range}</span>
                </div>
                <Input
                  type="number"
                  min={0}
                  value={drafts[p.period] ?? (p.ridership ?? "")}
                  onChange={(e) => setDrafts({ ...drafts, [p.period]: e.target.value })}
                  className="mt-2 h-10 font-mono text-lg text-right"
                  placeholder="0"
                  data-testid={`period-input-${p.period}`}
                  disabled={!isAdmin}
                />
                {!p.saved && p.suggested_from_daily > 0 && (
                  <div className="mt-1 text-[10px] text-blue-700">
                    Öneri (günlük toplam): <b>{fmtInt(p.suggested_from_daily)}</b>
                  </div>
                )}
                {p.saved && (
                  <div className="mt-1 text-[10px] text-emerald-700 font-mono">
                    ✓ Kaydedildi — {p.updated_by_name || "sistem"} · {(p.updated_at || "").slice(0, 10)}
                  </div>
                )}
                {isAdmin && (
                  <Button size="sm" onClick={() => savePeriod(p.period)} disabled={saving === p.period}
                          className="mt-2 w-full bg-[#1E3A8A] hover:bg-[#1e40af] text-white" data-testid={`period-save-${p.period}`}>
                    <Save className="w-3 h-3 mr-1" /> {saving === p.period ? "Kaydediliyor..." : "Kaydet"}
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 text-xs text-slate-500 leading-relaxed">
            <b>Not:</b> Bu ay için EN AZ BİR dönem kaydedilirse aylık toplam yalnızca dönemlerden hesaplanır. Günlük kayıtlar (varsa) yok sayılır — çift sayma önlenmiştir.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
